import { useMemo } from 'react';
import { HttpError } from '@refinedev/core';
import { useSuitabilityReports } from 'src/suitability-report/hooks/useSuitabilityReports';
import { useMeetingAgenda } from 'src/suitability-report/hooks/useMeetingAgendas';
import { useFactFinds } from 'src/suitability-report/hooks/useFactFinds';
import { BackgroundProcess } from 'src/data-capture/hooks/file-uploads/types';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';

type GeneratedDocument = {
  id: string;
  displayName: string;
  createdAt: string;
  documentType: 'suitability-report' | 'meeting-agenda' | 'fact-find';
  fileId?: number;
  backgroundProcess?: BackgroundProcess;
};

type UseGeneratedDocumentsReturn = {
  documents: GeneratedDocument[];
  isLoading: boolean;
  error: HttpError | null;
  refetch: () => Promise<void>;
};

export const useGeneratedDocuments = (): UseGeneratedDocumentsReturn => {
  const { clientId } = useCurrentClient();

  const {
    suitabilityReports,
    isLoading: suitabilityLoading,
    error: suitabilityError,
    refetch: refetchSuitability,
  } = useSuitabilityReports({ clientId });

  const {
    meetingAgendas,
    isLoading: agendasLoading,
    error: agendasError,
    refetch: refetchAgendas,
  } = useMeetingAgenda({ clientId });

  const {
    factFinds,
    isLoading: factFindsLoading,
    error: factFindsError,
    refetch: refetchFactFinds,
  } = useFactFinds();

  const documents = useMemo((): GeneratedDocument[] => {
    const allDocuments: GeneratedDocument[] = [];

    suitabilityReports.forEach(report => {
      allDocuments.push({
        ...report,
        displayName: report.displayName,
        documentType: 'suitability-report',
        fileId: report.reportFileId,
      });
    });

    meetingAgendas.forEach(agenda => {
      allDocuments.push({
        ...agenda,
        displayName: agenda.fileName || 'Meeting Agenda',
        documentType: 'meeting-agenda',
      });
    });

    factFinds.forEach(factFind => {
      allDocuments.push({
        ...factFind,
        displayName: factFind.fileName || 'Fact Find',
        documentType: 'fact-find',
        backgroundProcess: undefined,
      });
    });

    return allDocuments.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [suitabilityReports, meetingAgendas, factFinds]);

  const isLoading = suitabilityLoading || agendasLoading || factFindsLoading;
  const error = suitabilityError ?? agendasError ?? factFindsError;

  const refetch = async () => {
    await Promise.all([refetchSuitability(), refetchAgendas(), refetchFactFinds()]);
  };

  return {
    documents,
    isLoading,
    error,
    refetch,
  };
};
